import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const BillingAccountViewSchema = z.object({
  franchiseId: z.string(),
  kbAccountId: z.string(),
  currency: z.string(),
  billingEmail: z.string().nullable(),
  billingAddress: z.object({
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    city: z.string().nullable(),
    stateOrProvince: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string().nullable(),
  }),
  locale: z.string().nullable(),
  defaultPaymentMethodType: z.enum(["ADYEN_CARD", "EXTERNAL", "NONE"]),
  defaultPaymentMethodId: z.string().nullable(),
  accountBalance: z.number(),
  creditBalance: z.number(),
  isPastDue: z.boolean(),
});

const UpdateBillingDetailsSchema = z.object({
  billingEmail: z.string().email().optional(),
  name: z.string().optional(),
  locale: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  stateOrProvince: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

const SetPaymentMethodSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("ADYEN_CARD"),
    adyenTokenizationResult: z.string(),
    returnUrl: z.string().optional(),
  }),
  z.object({
    type: z.literal("EXTERNAL"),
  }),
]);

export const FranchiseBillingSummarySchema = z.object({
  franchiseId: z.string(),
  /** Subscription lifecycle: NONE = no subscription has ever been created. */
  subscriptionStatus: z.enum(["ACTIVE", "PENDING", "CANCELLING", "CANCELLED", "BLOCKED", "NONE"]),
  basePlanName: z.string().nullable(),
  /** ISO date of the next billing run, if known. */
  nextBillingDate: z.string().nullable(),
  isPastDue: z.boolean(),
  overdueLevel: z.string().nullable(),
  unreadInvoiceCount: z.number(),
  /** Derived health for at-a-glance triage in the salon list. */
  paymentHealth: z.enum(["HEALTHY", "OUTSTANDING", "OVERDUE", "SUSPENDED", "NONE"]),
  /** ISO timestamp of the last summary sync from KB. Stale > 5min = warn. */
  lastSyncedAt: z.string().nullable(),
});

const PaymentMethodSchema = z.object({
  paymentMethodId: z.string(),
  pluginName: z.string(),
  isDefault: z.boolean(),
  type: z.enum(["CARD", "EXTERNAL", "OTHER"]),
  last4: z.string().nullable(),
  brand: z.string().nullable(),
  expirationMonth: z.string().nullable(),
  expirationYear: z.string().nullable(),
});

const errorSchema = z.object({ message: z.string() });

export const franchiseBillingAccountContract = c.router({
  getAccount: {
    method: "GET",
    path: "/billing/accounts/:franchiseId",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: BillingAccountViewSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get the Kill Bill billing account for a franchise",
  },

  updateBillingDetails: {
    method: "PUT",
    path: "/billing/accounts/:franchiseId/details",
    pathParams: z.object({ franchiseId: z.string() }),
    body: UpdateBillingDetailsSchema,
    responses: {
      200: BillingAccountViewSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update franchise billing details and propagate to Kill Bill",
  },

  getTaxExempt: {
    method: "GET",
    path: "/billing/accounts/:franchiseId/tax-exempt",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: z.object({
        isTaxExempt: z.boolean(),
        taxExemptReason: z.string().nullable(),
      }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get the tax-exempt flag for a franchise",
  },

  setTaxExempt: {
    method: "PUT",
    path: "/billing/accounts/:franchiseId/tax-exempt",
    pathParams: z.object({ franchiseId: z.string() }),
    body: z.object({
      isTaxExempt: z.boolean(),
      reason: z.string().optional(),
    }),
    responses: {
      200: z.object({
        isTaxExempt: z.boolean(),
        taxExemptReason: z.string().nullable(),
      }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Set or clear the tax-exempt flag for a franchise (reason required when setting)",
  },

  getSuspensionState: {
    method: "GET",
    path: "/billing/accounts/:franchiseId/suspension",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: z.object({
        suspended: z.boolean(),
        stateName: z.string().nullable(),
      }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get the franchise account suspension state",
  },

  suspendFranchise: {
    method: "POST",
    path: "/billing/accounts/:franchiseId/suspend",
    pathParams: z.object({ franchiseId: z.string() }),
    body: z.object({ reason: z.string().optional() }),
    responses: {
      200: z.object({ suspended: z.boolean() }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Suspend the franchise account (block entitlement + billing)",
  },

  reinstateFranchise: {
    method: "POST",
    path: "/billing/accounts/:franchiseId/reinstate",
    pathParams: z.object({ franchiseId: z.string() }),
    body: z.object({}),
    responses: {
      200: z.object({ suspended: z.boolean() }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Lift suspension on the franchise account",
  },

  setPaymentMethod: {
    method: "PUT",
    path: "/billing/accounts/:franchiseId/payment-method",
    pathParams: z.object({ franchiseId: z.string() }),
    body: SetPaymentMethodSchema,
    responses: {
      200: z.object({ paymentMethodId: z.string(), type: z.string() }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Set or replace the default payment method for a franchise",
  },

  listPaymentMethods: {
    method: "GET",
    path: "/billing/accounts/:franchiseId/payment-methods",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: z.array(PaymentMethodSchema),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "List all payment methods on the franchise's KB account",
  },

  setDefaultPaymentMethod: {
    method: "PUT",
    path: "/billing/accounts/:franchiseId/payment-methods/:paymentMethodId/default",
    pathParams: z.object({ franchiseId: z.string(), paymentMethodId: z.string() }),
    body: z.object({}),
    responses: {
      200: z.object({
        paymentMethodId: z.string(),
        /** Auto-pay state after the change — backend toggles it based on the
         * default method's type (CARD → enabled, EXTERNAL → disabled). */
        autoPayEnabled: z.boolean(),
      }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Mark an existing payment method as the default; auto-pay is auto-toggled by type",
  },

  getAutoPay: {
    method: "GET",
    path: "/billing/accounts/:franchiseId/auto-pay",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: z.object({ enabled: z.boolean() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Check whether KB will auto-charge invoices on the franchise's account",
  },

  listBillingSummaries: {
    method: "GET",
    path: "/billing/franchises/billing-summaries",
    query: z.object({
      /** Comma-separated list of franchise IDs. */
      franchiseIds: z.string(),
    }),
    responses: {
      200: z.array(FranchiseBillingSummarySchema),
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Batch billing summary for a list of franchises (used by the salon list)",
  },

  setAutoPay: {
    method: "PUT",
    path: "/billing/accounts/:franchiseId/auto-pay",
    pathParams: z.object({ franchiseId: z.string() }),
    body: z.object({ enabled: z.boolean() }),
    responses: {
      200: z.object({ enabled: z.boolean() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary:
      "Enable or disable auto-payment on the franchise account (toggles AUTO_PAY_OFF tag in KB)",
  },
});
